import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient, formatApiError } from "@/lib/api";
import { formatGNF } from "@/utils/format";
import { toast } from "sonner";
import { Flame, TrendingUp, Clock } from "lucide-react";

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function cellColor(v, max) {
  if (max <= 0 || v <= 0) return "#F1EDE0"; // sand
  const t = Math.min(1, v / max);
  // green→yellow→red gradient
  if (t < 0.5) {
    // sand → yellow
    return `rgba(244, 196, 48, ${0.15 + t * 1.4})`;
  }
  return `rgba(227, 38, 54, ${0.35 + (t - 0.5) * 1.3})`;
}

export default function ProfitabilityHeatmap({ driverId }) {
  const { t } = useTranslation();
  const WEEKDAYS = [t("heat.wd0"), t("heat.wd1"), t("heat.wd2"), t("heat.wd3"), t("heat.wd4"), t("heat.wd5"), t("heat.wd6")];
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);

  const fetch = async (d = days) => {
    setLoading(true);
    try {
      const qs = driverId ? `?days=${d}&driver_id=${driverId}` : `?days=${d}`;
      const { data: r } = await apiClient.get(`/my/driver/profitability-heatmap${qs}`);
      setData(r);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(days); /* eslint-disable-next-line */ }, [days, driverId]);

  const max = data ? Math.max(1, ...data.matrix.flat()) : 1;
  const bestLabel = data?.best_slot?.weekday != null
    ? `${WEEKDAYS[data.best_slot.weekday]} · ${String(data.best_slot.hour).padStart(2, "0")}h`
    : "—";

  return (
    <div className="space-y-4" data-testid="profitability-heatmap">
      <div className="bg-white border border-sand rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand-red font-semibold flex items-center gap-1.5">
            <Flame className="w-3.5 h-3.5" /> {t("heat.title")}
          </div>
          <div className="text-sm text-mute">{t("heat.sub")}</div>
        </div>
        <div className="flex gap-1">
          {[30, 90, 180].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`heatmap-days-${d}`}
              className={`text-xs rounded-full px-3 py-1 border ${days === d ? "bg-ink text-white border-ink" : "bg-white text-ink border-sand"}`}
            >
              {t("heat.daysBtn", { d })}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-mute text-sm py-8 text-center">{t("heat.loading")}</div>
      ) : !data || data.total_orders === 0 ? (
        <div className="bg-white border border-sand rounded-2xl p-8 text-center text-mute text-sm">
          {t("heat.empty")}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="bg-white border border-sand rounded-2xl p-4">
              <div className="text-xs uppercase tracking-widest text-mute">{t("heat.total")}</div>
              <div className="font-display text-2xl font-semibold">{formatGNF(data.total_earnings)}</div>
            </div>
            <div className="bg-white border border-sand rounded-2xl p-4">
              <div className="text-xs uppercase tracking-widest text-mute">{t("heat.delivered")}</div>
              <div className="font-display text-2xl font-semibold">{data.total_orders}</div>
            </div>
            <div className="bg-white border border-sand rounded-2xl p-4">
              <div className="text-xs uppercase tracking-widest text-mute flex items-center gap-1.5">
                <TrendingUp className="w-3 h-3" /> {t("heat.best")}
              </div>
              <div className="font-display text-2xl font-semibold">{bestLabel}</div>
              <div className="text-xs text-brand-green font-semibold">{formatGNF(data.best_slot.amount)}</div>
            </div>
          </div>

          <div className="bg-white border border-sand rounded-2xl p-4 overflow-x-auto" data-testid="heatmap-grid">
            <div className="min-w-[720px]">
              <div className="grid grid-cols-[36px_repeat(24,minmax(0,1fr))] gap-0.5">
                <div />
                {HOURS.map((h) => (
                  <div key={h} className="text-[9px] text-mute text-center font-mono">{String(h).padStart(2, "0")}</div>
                ))}
                {WEEKDAYS.map((wd, wi) => (
                  <React.Fragment key={wd}>
                    <div className="text-[10px] text-mute font-semibold flex items-center">{wd}</div>
                    {HOURS.map((h) => {
                      const v = data.matrix[wi][h];
                      const count = data.counts[wi][h];
                      return (
                        <div
                          key={h}
                          className="aspect-square rounded-[3px] relative group"
                          style={{ backgroundColor: cellColor(v, max) }}
                          data-testid={`heatmap-cell-${wi}-${h}`}
                          title={`${wd} ${String(h).padStart(2, "0")}h · ${count} ${t("heat.runs")} · ${v.toLocaleString("fr-FR")} GNF`}
                        />
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-3 text-[10px] text-mute">
                <span>{t("heat.low")}</span>
                <div className="flex-1 h-2 rounded-full" style={{ background: "linear-gradient(90deg,#F1EDE0,#F4C430,#E32636)" }} />
                <span>{t("heat.high")}</span>
              </div>
            </div>
          </div>

          <div className="bg-[#FFF7E6] border border-brand-yellow/40 rounded-2xl p-4 flex items-start gap-3">
            <Clock className="w-4 h-4 text-[#B57F00] mt-0.5 shrink-0" />
            <div className="text-sm">
              <b>{t("heat.tip")}</b> {t("heat.tipBe")} <b>{bestLabel}</b> {t("heat.tipEnd", { days })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
